<script lang="ts" setup>
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import type { StockShortage } from '~/server/utils/stockShortages'

const props = withDefaults(
  defineProps<{
    visible: boolean
    shortages: StockShortage[]
    // the cart being charged: the server checks the counts against it
    cart?: unknown[]
    // false hides the count inputs (employee without can_update_stock)
    canAdjust?: boolean
  }>(),
  { canAdjust: true },
)

const emit = defineEmits<{
  'update:visible': [value: boolean]
  // Counts were saved; the caller retries the order
  'adjusted': []
}>()

const { t } = useNuxtApp().$i18n
const buuid = useCookie('buuid')
const { post } = useAuthenticatedFetch()

const saving = ref(false)
const counts = ref<Record<string, number | null>>({})

const dialogVisible = computed({
  get: () => props.visible,
  set: (val: boolean) => emit('update:visible', val),
})

// Fresh inputs for every new shortage list
watch(() => props.shortages, () => {
  counts.value = {}
})

const hasCounts = computed(() => Object.values(counts.value).some(v => v !== null && v !== undefined))

function usedInLabel(s: StockShortage): string {
  return s.used_in.filter(name => name !== s.name).join(', ')
}

async function saveAndRetry() {
  if (!hasCounts.value) return
  saving.value = true
  try {
    await post(`/api/business/${buuid.value}/inventory/bulk-update`, { ...buildShortageAdjustPayload(props.shortages, counts.value), cart: props.cart })
    useSuccessToast('Stock adjusted successfully')
    emit('adjusted')
  }
  catch {
    useErrorToast('Failed to adjust stock')
  }
  finally {
    saving.value = false
  }
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="t('Not enough stock')"
    modal
    :style="{ width: '480px' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    :closable="!saving"
    :close-on-escape="!saving"
  >
    <div
      class="flex flex-col gap-4"
      data-testid="stock-shortage-dialog"
    >
      <p class="text-sm text-gray-600 m-0">
        {{ canAdjust
          ? t('Enter the real count of each item to correct the stock and charge again.')
          : t('Ask someone with permission to update stock to correct these items.') }}
      </p>

      <div
        v-for="s in shortages"
        :key="shortageKey(s)"
        class="flex flex-col gap-2 border-b pb-3 last:border-b-0"
        data-testid="stock-shortage-row"
      >
        <div class="flex items-baseline justify-between gap-3">
          <div class="min-w-0">
            <div class="font-semibold text-gray-900">
              {{ s.name }}
            </div>
            <div
              v-if="usedInLabel(s)"
              class="text-xs text-gray-500"
            >
              {{ t('Used in') }}: {{ usedInLabel(s) }}
            </div>
          </div>
          <div
            v-if="s.available !== null"
            class="font-mono text-sm whitespace-nowrap text-red-600"
          >
            {{ s.available }} / {{ s.required }} {{ s.unit ?? '' }}
          </div>
        </div>

        <div
          v-if="canAdjust"
          class="flex items-center gap-3"
        >
          <label
            :for="`shortage-count-${shortageKey(s)}`"
            class="text-sm text-gray-700 whitespace-nowrap"
          >
            {{ t('Real count') }}
          </label>
          <InputNumber
            v-model="counts[shortageKey(s)]"
            :input-id="`shortage-count-${shortageKey(s)}`"
            :min="0"
            :max-fraction-digits="3"
            :suffix="s.unit ? ` ${s.unit}` : ''"
            class="w-full"
            @input="(e) => counts[shortageKey(s)] = typeof e.value === 'number' ? e.value : null"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('Cancel')"
          severity="secondary"
          :disabled="saving"
          @click="dialogVisible = false"
        />
        <Button
          v-if="canAdjust"
          :label="t('Save and charge again')"
          icon="pi pi-check-circle"
          :loading="saving"
          :disabled="!hasCounts"
          data-testid="stock-shortage-save"
          @click="saveAndRetry"
        />
      </div>
    </template>
  </Dialog>
</template>
